'use client'

import { forwardRef } from 'react'

import { LuLoaderCircle } from 'react-icons/lu'

import { cn } from '@/core/utils/common'
import { useHydrated } from '@/core/hooks/use-hydrated'

import {
  sizeStyles,
  variantStyles,
  type ButtonSize,
  type ButtonVariant,
} from './button-styles'

import type { ButtonHTMLAttributes } from 'react'

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      disabled,
      children,
      className,
      ...props
    },
    ref
  ) => {
    const hydrated = useHydrated()
    const isDisabled =
      disabled || loading || (!hydrated && typeof props.onClick === 'function')

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        aria-busy={loading || undefined}
        className={cn(
          'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-field font-medium',
          'transition-[color,background-color,border-color,opacity] duration-200',
          'active:opacity-80 focus:outline-none focus-visible:opacity-90',
          'disabled:cursor-not-allowed disabled:opacity-50 disabled:active:opacity-50',
          variantStyles[variant],
          sizeStyles[size],
          className
        )}
        {...props}
      >
        {loading && (
          <LuLoaderCircle aria-hidden="true" className="size-4 animate-spin" />
        )}
        {children}
      </button>
    )
  }
)

Button.displayName = 'Button'
